/**
 * Inbox: dispatch chat, plus the support ChowDash used to split across two more
 * screens.
 *
 * A rider reaches this page when something needs a human: a payout question, an
 * offer that looks wrong, or something urgent. So the conversation and the help
 * that answers it belong on one screen — asking "where do I get help" should not
 * be a navigation problem.
 *
 * The page does not use the shell's fill layout. Fill pins the composer to the
 * viewport, which is right for a chat-only screen and wrong here: the support
 * section below the thread has to be reachable by scrolling.
 */
import { useMemo, useState } from 'react'
import {
  MinimalAlert,
  MinimalInfoPanel,
} from '@ovasabi/ui-minimal'
import { ChatIcon, RouteIcon, ShieldIcon } from '../../components/icons'
import {
  ConversationPanel,
  PageHeader,
  Screen,
  type ConversationMessage,
} from '../../components/chow'
import {
  useRiderLocationSnapshot,
  useSupportMessageSnapshot,
} from '../../generated/prototypeRuntime'
import { commands, isLiveRuntime } from '../../lib/runtime'
import { compareTimestamps, formatRFC3339UTC } from '@ovasabi/frontend-kit'
import { EmergencySos } from './EmergencySos'
import {
  getStore,
  number,
  text,
  useRiderId,
  type Snapshot,
} from './records'
import { riderStyle as Style } from './riderStyle'

const RIDER_THREAD_PREFIX = 'rider-'

export const InboxPage = () => {
  const riderId = useRiderId()
  const snapshot = useSupportMessageSnapshot() as Snapshot
  const locations = useRiderLocationSnapshot() as Snapshot
  const [sendError, setSendError] = useState('')
  const threadId = text(snapshot.records[0] ?? {}, ['thread_id'], `${RIDER_THREAD_PREFIX}${riderId}`)

  const messages = useMemo<ConversationMessage[]>(
    () =>
      snapshot.records
        .filter((record) => text(record, ['thread_id'], threadId) === threadId)
        .slice()
        .sort((a, b) => compareTimestamps(text(a, ['created_at']), text(b, ['created_at']), text(a, ['id']), text(b, ['id'])))
        .map((record, index) => ({
          id: text(record, ['id'], `support-${index}`),
          body: text(record, ['body'], 'Dispatch update'),
          senderId: text(record, ['sender_id']),
          senderLabel: text(record, ['sender_type'], 'Dispatch'),
          createdAt: formatRFC3339UTC(text(record, ['created_at'])) || undefined,
        })),
    [snapshot.records, threadId],
  )

  const send = async (body: string) => {
    setSendError('')
    const payload = { thread_id: threadId, sender_id: riderId, sender_type: 'rider', body }
    try {
      if (isLiveRuntime()) await commands.support.sendMessage(payload)
      else {
        void commands.support.sendMessage(payload).catch(() => undefined)
        getStore('support.support_messages')?.upsert({
          id: `support-${Math.random().toString(36).slice(2)}`,
          organization_id: 'org-prototype',
          created_at: new Date().toISOString(),
          ...payload,
        })
      }
    } catch {
      setSendError('That message was not delivered. Check your connection and send it again.')
    }
  }

  const location = locations.records[0] ?? {}
  const hasFix = locations.records.length > 0

  return (
    <Screen header={<PageHeader title="Messages" accentDot />}>
      {sendError ? <MinimalAlert tone="danger" title="Message not sent">{sendError}</MinimalAlert> : null}

      <ConversationPanel
        messages={messages}
        currentActorId={riderId}
        onSend={send}
        emptyTitle="Dispatch is ready when you need it"
        emptyDescription="Ask about an offer, active delivery, payout or account issue."
        placeholder="Message dispatch…"
      />

      <Style.SectionTitle><ChatIcon />Support</Style.SectionTitle>

      <MinimalInfoPanel
        tone={hasFix ? 'success' : 'warning'}
        icon={<ShieldIcon />}
        eyebrow="Safety connection"
        title={hasFix ? 'Location sharing is available' : 'Location has not been received'}
        description={
          hasFix
            ? `${number(location, ['latitude']).toFixed(4)}, ${number(location, ['longitude']).toFixed(4)} — dispatch can see where you are while you dash.`
            : 'Enable location access before starting a dash so emergency support can find you.'
        }
        action={<EmergencySos />}
      />

      <MinimalInfoPanel
        tone="info"
        icon={<RouteIcon />}
        eyebrow="Delivery flow"
        title="Pickup, travel and hand-off stay on Jobs"
        description="Accept an offer there. Each hand-off is confirmed in order, with the customer PIN at drop-off."
      />
    </Screen>
  )
}
